// The browser's half of the two verbs. The whole cart goes up on every change, so every
// save is idempotent and the store never has to merge.

import type { CartLine } from "./cart";

/** What the cart store holds for one character. A null fingerprint means it holds nothing. */
export interface StoredRow {
  fingerprint: string | null;
  done: boolean;
  cart: { lines?: CartLine[]; unequipped?: string[] } | null;
}

export interface Held {
  lines: CartLine[];
  /** Sheet ids of the arrived rows left at home. */
  unequipped: string[];
  done: boolean;
}

const NOTHING: Held = { lines: [], unequipped: [], done: false };

export async function readRow(slug: string): Promise<StoredRow> {
  try {
    const response = await fetch(`/api/cart/${slug}`);
    if (!response.ok) return { fingerprint: null, done: false, cart: null };
    return (await response.json()) as StoredRow;
  } catch {
    return { fingerprint: null, done: false, cart: null };
  }
}

let queue: Promise<void> = Promise.resolve();

/** Overlapping saves can land out of order, so writes go up one at a time and the last tap
 * stays the last write. */
export function writeRow(slug: string, fingerprint: string, held: Held): Promise<void> {
  queue = queue.then(async () => {
    try {
      await fetch(`/api/cart/${slug}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          fingerprint,
          done: held.done,
          cart: { lines: held.lines, unequipped: held.unequipped },
        }),
      });
    } catch {
      // Pages still load when the cart store is down; only saving breaks.
    }
  });
  return queue;
}

/** A fingerprint that is not yours means the character's file has moved on, so take none of
 * it and let the next write replace the row. */
export function held(row: StoredRow, fingerprint: string): Held {
  if (row.fingerprint !== fingerprint) return NOTHING;
  return {
    lines: row.cart?.lines ?? [],
    unequipped: row.cart?.unequipped ?? [],
    done: row.done,
  };
}
